import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import './Hearing.css';

const HearingScheduling = () => {
  const { hearings, setHearings, cases, courts, judges, addAuditLog } = useApp();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    caseId: '',
    hearingDate: '',
    hearingTime: '',
    courtId: '',
    judgeId: '',
    benchName: '',
    purpose: '',
    status: 'Scheduled',
  });
  const [editingId, setEditingId] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const caseItem = cases.find(c => c.id === formData.caseId);
    const court = courts.find(c => c.id === formData.courtId);
    const judge = judges.find(j => j.id === formData.judgeId);
    
    if (editingId) {
      setHearings(hearings.map(h => 
        h.id === editingId 
          ? { 
              ...h, 
              ...formData,
              caseNumber: caseItem?.caseNumber || h.caseNumber,
              caseTitle: caseItem?.caseTitle || h.caseTitle,
              courtName: court?.courtName || h.courtName,
              judgeName: judge?.judgeName || h.judgeName,
            }
          : h
      ));
      addAuditLog('Hearing Updated', `Hearing updated for case ${caseItem?.caseNumber}`);
    } else {
      const newHearing = {
        id: Date.now().toString(),
        ...formData,
        caseNumber: caseItem?.caseNumber || '',
        caseTitle: caseItem?.caseTitle || '',
        courtName: court?.courtName || '',
        judgeName: judge?.judgeName || '',
        createdAt: new Date().toISOString(),
      };
      setHearings([...hearings, newHearing]);
      addAuditLog('Hearing Scheduled', `Hearing scheduled for case ${caseItem?.caseNumber}`);
    }
    
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      caseId: '',
      hearingDate: '',
      hearingTime: '',
      courtId: '',
      judgeId: '',
      benchName: '',
      purpose: '',
      status: 'Scheduled',
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (hearing) => {
    setFormData(hearing);
    setEditingId(hearing.id);
    setShowForm(true);
  };

  const handleDelete = (id) => {
    const hearing = hearings.find(h => h.id === id);
    setHearings(hearings.filter(h => h.id !== id));
    addAuditLog('Hearing Deleted', `Hearing deleted for case ${hearing.caseNumber}`);
  };

  return (
    <div className="hearing-container">
      <div className="hearing-header">
        <h1>📅 Hearing Scheduling</h1>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary">
          {showForm ? 'Cancel' : '+ Schedule Hearing'}
        </button>
      </div>

      {showForm && (
        <div className="hearing-form-card">
          <h2>{editingId ? 'Edit Hearing' : 'Schedule New Hearing'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label>Case *</label>
                <select
                  value={formData.caseId}
                  onChange={(e) => setFormData({...formData, caseId: e.target.value})}
                  required
                >
                  <option value="">Select Case</option>
                  {cases.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.caseNumber} - {c.caseTitle}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Hearing Date *</label>
                <input
                  type="date"
                  value={formData.hearingDate}
                  onChange={(e) => setFormData({...formData, hearingDate: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Hearing Time *</label>
                <input
                  type="time"
                  value={formData.hearingTime}
                  onChange={(e) => setFormData({...formData, hearingTime: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Court *</label>
                <select
                  value={formData.courtId}
                  onChange={(e) => setFormData({...formData, courtId: e.target.value})}
                  required
                >
                  <option value="">Select Court</option>
                  {courts.map(court => (
                    <option key={court.id} value={court.id}>{court.courtName}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Judge *</label>
                <select
                  value={formData.judgeId}
                  onChange={(e) => setFormData({...formData, judgeId: e.target.value})}
                  required
                >
                  <option value="">Select Judge</option>
                  {judges.map(judge => (
                    <option key={judge.id} value={judge.id}>
                      {judge.judgeName} - {judge.courtName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Bench Name</label>
                <input
                  type="text"
                  value={formData.benchName}
                  onChange={(e) => setFormData({...formData, benchName: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label>Status *</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  required
                >
                  <option value="Scheduled">Scheduled</option>
                  <option value="Completed">Completed</option>
                  <option value="Postponed">Postponed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>

              <div className="form-group full-width">
                <label>Purpose</label>
                <textarea
                  value={formData.purpose}
                  onChange={(e) => setFormData({...formData, purpose: e.target.value})}
                  rows="3"
                  placeholder="Enter hearing purpose"
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingId ? 'Update' : 'Schedule'} Hearing
              </button>
              <button type="button" onClick={resetForm} className="btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="hearing-table-card">
        <h2>Scheduled Hearings ({hearings.length})</h2>
        {hearings.length > 0 ? (
          <div className="table-container">
            <table className="hearing-table">
              <thead>
                <tr>
                  <th>Case Number</th>
                  <th>Case Title</th>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Court</th>
                  <th>Judge</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {hearings.sort((a, b) => new Date(a.hearingDate) - new Date(b.hearingDate)).map(hearing => (
                  <tr key={hearing.id}>
                    <td><strong>{hearing.caseNumber}</strong></td>
                    <td>{hearing.caseTitle}</td>
                    <td>{new Date(hearing.hearingDate).toLocaleDateString()}</td>
                    <td>{hearing.hearingTime}</td>
                    <td>{hearing.courtName}</td>
                    <td>{hearing.judgeName}</td>
                    <td>
                      <span className={`status-badge status-${hearing.status.toLowerCase()}`}>
                        {hearing.status}
                      </span>
                    </td>
                    <td>
                      <button onClick={() => handleEdit(hearing)} className="btn-edit">Edit</button>
                      <button onClick={() => handleDelete(hearing.id)} className="btn-delete">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="empty-state">No hearings scheduled yet. Click "Schedule Hearing" to get started.</p>
        )}
      </div>
    </div>
  );
};

export default HearingScheduling;

